import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useAuth } from "shared/lib/useAuth"

import { useAllProjectPermissionsQuery } from "entities/projects/graphql/queries.generated"
import { TeamRole, isTeamRole } from "entities/team-members/types/TeamRole"
import { useProjectSearch } from "features/global-search/api/useProjectSearch"
import {
  GlobalSearchResultType,
  ProjectOptionsGroup
} from "features/global-search/types/GlobalSearch"

interface UseProjectOptionsGroupArgs {
  searchTerm: string | null
}

export function useProjectOptionsGroup({
  searchTerm
}: UseProjectOptionsGroupArgs): {
  projectOptions: ProjectOptionsGroup | null
  isLoading: boolean
} {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const { results: projects, isLoading } = useProjectSearch({ searchTerm })

  const { data: permissionsData } = useAllProjectPermissionsQuery({
    skip: !isAuthenticated
  })

  const roleByProjectId = useMemo(() => {
    const map = new Map<string, TeamRole>()
    permissionsData?.allProjectPermissions?.forEach(p => {
      map.set(p.resourceId, isTeamRole(p.role) ? p.role : TeamRole.READ)
    })
    return map
  }, [permissionsData])

  const projectOptions = useMemo<ProjectOptionsGroup | null>(() => {
    if (projects.length === 0) return null
    return {
      type: GlobalSearchResultType.PROJECT,
      label: t("Project.Projects"),
      options: projects.map(({ project, matchInfo }) => {
        return {
          id: project.id,
          value: project.id,
          label: project.title,
          type: GlobalSearchResultType.PROJECT,
          data: {
            project,
            role: roleByProjectId.get(project.id) ?? TeamRole.READ,
            matchInfo
          }
        }
      })
    }
  }, [projects, roleByProjectId, t])

  return { projectOptions, isLoading }
}
